/**
 * Maps authEnvironment strings to SSAS base URLs.
 * Used by both Node.js and browser helpers to resolve authEnvironment → URL.
 *
 * Priority: ssasBaseUrl (explicit override) > authEnvironment lookup
 */
export const SSAS_URLS = {
  prod: 'https://auth.shipstation.com',

  stage: 'https://auth-stage.shipstation.com',
  'stage-sslocal': 'https://auth-stage.sslocal.com',

  dev: 'https://auth-dev.shipstation.com',
  'dev-sslocal': 'https://auth-dev.sslocal.com',

  local: 'https://auth-local.shipstation.com',

  'local-z': 'https://auth-local-z.shipstation.com',
  'local-z-sslocal': 'https://auth-local-z.sslocal.com',

  'local-z2': 'https://auth-local-z2.shipstation.com',
  'local-z2-sslocal': 'https://auth-local-z2.sslocal.com',

  'local-z3': 'https://auth-local-z3.shipstation.com',
  'local-z3-sslocal': 'https://auth-local-z3.sslocal.com',

  'local-ssas': 'https://local-ssas.shipstation.com',
  'local-ssas-sslocal': 'https://local-ssas.sslocal.com',
};

/**
 * Resolves an authEnvironment string to a SSAS base URL.
 * @param {string} authEnvironment
 * @returns {string} SSAS base URL (trailing slash removed)
 * @throws {Error} if authEnvironment is not recognised
 */
export function getSsasBaseUrl(authEnvironment) {
  const url = SSAS_URLS[authEnvironment];
  if (!url) {
    throw new Error(`Unknown authEnvironment: "${authEnvironment}". Valid values: ${Object.keys(SSAS_URLS).join(', ')}`);
  }
  return url;
}

/**
 * Resolves the correct SSAS base URL at runtime using the current page hostname.
 *
 * Selects the first entry in `hostnames` whose suffix matches `currentHostname`,
 * falling back to the first entry if none match. This allows a single CDN bundle
 * to work correctly on both shipstation.com and sslocal.com deployments without
 * needing separate builds or baked-in single-domain URLs.
 *
 * @param {string} subdomain - SSAS subdomain (e.g. 'auth-dev', 'auth-stage', 'auth')
 * @param {string} hostnames - Comma-separated ordered list of valid TLDs (e.g. 'shipstation.com,sslocal.com')
 * @param {string} currentHostname - The current page hostname (e.g. window.location.hostname)
 * @returns {string} SSAS base URL (e.g. 'https://auth-dev.sslocal.com')
 */
export function resolveSsasBaseUrl(subdomain, hostnames, currentHostname) {
  const hosts = hostnames.split(',').map(h => h.trim());
  const match = hosts.find(h => currentHostname.endsWith(h)) ?? hosts[0];
  return `https://${subdomain}.${match}`;
}
